import React, { useState } from 'react';
import { Deal, PipelineStage, pipelineAPI } from '../../services/api';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Modal, Button, Card, CardBody } from '../ui';

interface DealStageModalProps {
  deal: Deal;
  stages: PipelineStage[];
  onClose: () => void;
}

const DealStageModal: React.FC<DealStageModalProps> = ({ deal, stages, onClose }) => {
  const [selectedStageId, setSelectedStageId] = useState<number>(deal.stageId || 0);
  const [probability, setProbability] = useState<number>(deal.probability || 50);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStageChange = (stageId: number) => {
    setSelectedStageId(stageId);
    const stage = stages.find(s => s.id === stageId);
    if (stage) {
      setProbability(stage.defaultProbability);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedStageId === deal.stageId) {
      setError('Please select a different stage');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await pipelineAPI.moveDealStage(deal.id, {
        toStageId: selectedStageId,
        probability,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to move deal');
    } finally {
      setLoading(false);
    }
  };

  const currentStage = stages.find(s => s.id === deal.stageId);
  const targetStage = stages.find(s => s.id === selectedStageId);

  return (
    <Modal isOpen={true} onClose={onClose} title="Move Deal to Stage" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Deal Info */}
        <Card variant="bordered">
          <CardBody className="p-4">
            <h4 className="text-sm font-medium text-neutral-900 mb-2">
              {deal.description}
            </h4>
            <div className="flex items-center justify-between text-sm text-neutral-600">
              <span>{deal.client?.name}</span>
              <span className="font-semibold text-primary-600">
                ${Number(deal.amount).toLocaleString()}
              </span>
            </div>
          </CardBody>
        </Card>

        {/* Stage Transition */}
        <div className="flex items-center justify-center space-x-4 py-4">
          <div className="text-center">
            <div className="text-xs text-neutral-500 mb-1">Current Stage</div>
            <div className="flex items-center space-x-2">
              {currentStage?.color && (
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: currentStage.color }}
                />
              )}
              <span className="text-sm font-medium text-neutral-900">
                {currentStage?.name || 'None'}
              </span>
            </div>
          </div>
          <ArrowRightIcon className="h-5 w-5 text-neutral-400" />
          <div className="text-center">
            <div className="text-xs text-neutral-500 mb-1">New Stage</div>
            <div className="flex items-center space-x-2">
              {targetStage?.color && (
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: targetStage.color }}
                />
              )}
              <span className="text-sm font-medium text-primary-600">
                {targetStage?.name || 'Select...'}
              </span>
            </div>
          </div>
        </div>

        {/* Stage Selection */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Select Stage *
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {stages.map((stage) => (
              <label
                key={stage.id}
                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedStageId === stage.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                <input
                  type="radio"
                  name="stage"
                  value={stage.id}
                  checked={selectedStageId === stage.id}
                  onChange={() => handleStageChange(stage.id)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center space-x-2">
                    {stage.color && (
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: stage.color }}
                      />
                    )}
                    <span className="text-sm font-medium text-neutral-900">
                      {stage.name}
                    </span>
                    <span className="text-xs text-neutral-500">
                      ({stage.defaultProbability}%)
                    </span>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Probability */}
        <div>
          <label className="block text-sm font-medium text-neutral-700">
            Win Probability (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={probability}
            onChange={(e) => setProbability(parseInt(e.target.value) || 0)}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-neutral-700">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder="Add notes about this stage change..."
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!selectedStageId}
            isLoading={loading}
          >
            Move Deal
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default DealStageModal;

